import type {
  AccessTokenDto,
  ApiSubscriberInfoDtoAccountSingleView as SubscriberInfoDto,
  PasswordPolicy,
} from '@bldsoft/cappie/api/wbs'
import type { ApiEffect } from '../index.h'
import type { Session } from './session'

import { createEffect, sample } from 'effector'
import { set as setSession } from './session'

export const SubscriberGender = {
  Male: 0,
  Female: 1,
  Other: 2,
  Unknown: 3,
} as const
export type SubscriberGender =
  (typeof SubscriberGender)[keyof typeof SubscriberGender]

export const SubscriberRegistrationStatus = {
  IN_PROCESS: 'IN_PROCESS',
  DONE: 'DONE',
  FAILED: 'FAILED',
  WAITING_EMAIL_CONFIRMATION: 'WAITING_EMAIL_CONFIRMATION',
  EXPIRED: 'EXPIRED',
} as const
export type SubscriberRegistrationStatus =
  (typeof SubscriberRegistrationStatus)[keyof typeof SubscriberRegistrationStatus]

export interface Subscriber extends Omit<SubscriberInfoDto, 'gender'> {
  name: string
  gender: SubscriberGender
  phone?: string
  birthday?: Date
  profileSwitchLocked?: boolean
}

export interface ResetPasswordSession {
  id: string
  time_period: string
  time_value: number
  passwordPolicy?: PasswordPolicy | null
}

interface InitResetPasswordData {
  email: string
}

export const PasswordPolicyName = {
  DEFAULT: 'DEFAULT',
  STRONG: 'STRONG',
} as const
export type PasswordPolicyName =
  (typeof PasswordPolicyName)[keyof typeof PasswordPolicyName]

export interface Features {
  pushNotificationEnabled?: boolean
  aiSearchAvailable?: boolean
  appearanceCustomizationEnabled?: boolean
  countryLocation?: { country: string }
  guestModeEnabled?: boolean
  inAppCredentialsChangeEnabled?: boolean
  partnersProgramEnabled?: boolean
  registrationEnabled?: boolean
  resetPasswordEnabled?: boolean
  signInWithPhoneEnabled?: boolean
  kidsModulesEnabled?: boolean
  passwordPolicy?: PasswordPolicy | null
}

export interface AccountAccessToken extends Required<AccessTokenDto> {}

export interface UpdateSubscriberUsernameData {
  username: string
}

export interface UpdateSubscriberPasswordData {
  password: string
  newPassword: string
  logoutAfterPasswordChange: boolean
}

//*
//* EFFECTS
//*

export const getFeaturesFx: ApiEffect<void, Features> = createEffect()

export const signUpFx: ApiEffect<
  { email: string; password: string; username: string },
  {
    id: string
    time_value: number
    time_period: string
  }
> = createEffect()

export const signUpByPhoneFx: ApiEffect<
  { phoneNumber: string },
  {
    id: string
    time_value: number
    time_period: string
  }
> = createEffect()

export const getRegistrationStatusFx: ApiEffect<
  { id: string },
  { status: SubscriberRegistrationStatus }
> = createEffect()

export const resendConfirmationEmailFx: ApiEffect<{ id: string }, void> =
  createEffect()

export const resendConfirmationPhoneFx: ApiEffect<{ id: string }, void> =
  createEffect()

export const confirmCodeRegistrationFx: ApiEffect<
  { id: string; code: string },
  { firstName: string; lastName: string; email: string }
> = createEffect()

export const confirmCodeRegistrationByPhoneFx: ApiEffect<
  { id: string; code: string; rememberMe: boolean },
  { code: number; payload: Session }
> = createEffect()

export const initResetPasswordFx: ApiEffect<
  InitResetPasswordData,
  ResetPasswordSession
> = createEffect()

export const verifyResetPasswordCodeFx: ApiEffect<
  { id: string; code: string },
  void
> = createEffect()

export const resendResetPasswordCodeFx: ApiEffect<{ id: string }, void> =
  createEffect()

export const setNewPasswordFx: ApiEffect<
  { id: string; code: string; password: string },
  void
> = createEffect()

export const getAccountAccessTokenFx: ApiEffect<void, AccountAccessToken> =
  createEffect()
export const getFx: ApiEffect<void, Subscriber> = createEffect()
export const updateProfileSwitchLockedFx: ApiEffect<boolean, void> =
  createEffect()

export const updateSubscriberUsernameFx: ApiEffect<
  UpdateSubscriberUsernameData,
  void
> = createEffect()

export const updateSubscriberPasswordFx: ApiEffect<
  UpdateSubscriberPasswordData,
  void
> = createEffect()

//*
//* CONNECTIONS
//*

//*
//* STORES
//*

sample({
  clock: confirmCodeRegistrationByPhoneFx.doneData,
  filter: ({ code, payload }) => code === 200 && payload != null,
  fn: ({ payload }) => payload,
  target: setSession,
})
